import React from 'react';

import './PointsWatching.css';

import withShipment from '../../withShipment';
import FeedbackBar from '../feedbackBar/FeedbackBar';
import CustomToolbar from '../global/CustomToolbar';

import { MODE, ALL_FILTER, PHOTO_FILTER, C } from './constants';
import { empMeta, displayName } from './employeeColors';
import { toYMD, parseYMD, weekdayOf, labelOf } from './pointsWatchingHelpers';
import {
  Avatar,
  Banner,
  EmptyPrompt,
  SearchBox,
  EntryCard,
  EmployeeDayCard,
  Lightbox,
  PhotoQualityChips,
} from './PointsWatchingParts';

// redux
import { statusSelector } from '../../redux/selectors/global';
import { clearStatus } from '../../redux/actions/global';
import {
  employeesSelector,
  userInfoSelector,
} from '../../redux/selectors/auth';
import { getEmployees } from '../../redux/actions/auth';
import {
  getPointWatchingByDate,
  getPointWatchingEmployeeDays,
  getPointWatchingDayEntries,
  clearPointWatchingAlert,
} from '../../redux/actions/settings';
import {
  pointWatchingByDateSelector,
  pointWatchingEmployeeDaysSelector,
  pointWatchingDayEntriesSelector,
} from '../../redux/selectors/customer';

// mui
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

import EventIcon from '@material-ui/icons/Event';
import PersonIcon from '@material-ui/icons/Person';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';

class PointsWatching extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: MODE.SESSION,
      dateValue: null, // Date | null (By session)
      empValue: null, // employee object | null (By employee)
      empDay: null, // chosen day object (By employee, State C)
      byFilter: ALL_FILTER, // ALL | userId
      photoFilter: PHOTO_FILTER.ALL, // ALL | CLEAR | UNCLEAR
      query: '',
      lightbox: { open: false, src: null, label: null },
      employees: [],
      feedbackBarOpen: false,
      feedbackStatus: '',
      feedbackMessage: '',
    };
  }

  componentDidMount() {
    const locationId = this.locationId();
    this.props.getEmployees({ locationId });
    // Opened from a "no clear subject" navbar alert — jump straight to
    // By session for that date with the giving employee pre-filtered.
    this.applyDeepLink();
  }

  componentDidUpdate(prevProps) {
    if (
      this.props.status.statusText &&
      prevProps.status.statusText !== this.props.status.statusText
    ) {
      this.openFeedbackBar(this.props.status.statusText, 'error');
    }

    // Re-fire the deep link when navigated here again while already mounted
    // (clicking another alert pushes the same route with new state).
    const prevKey = prevProps.location && prevProps.location.key;
    const curKey = this.props.location && this.props.location.key;
    if (curKey !== prevKey) this.applyDeepLink();

    if (prevProps.employees !== this.props.employees) {
      const filteredEmployees = (this.props.employees || []).filter(
        (employee) => {
          const roles = (employee.authorities || []).map(
            (auth) => auth.authority
          );
          if (roles.includes('ROLE_ADMIN')) return false;
          if (
            roles.length === 2 &&
            roles.includes('ROLE_DASHBOARD') &&
            roles.includes('ROLE_USER')
          )
            return false;
          return true;
        }
      );
      this.setState({ employees: filteredEmployees });
    }
  }

  locationId = () => this.props.user && this.props.user.selectedLocationId;

  // Honor a deep link from the navbar "no clear subject" alert:
  // open By session for the alert's date with the giving employee's chip
  // filter pre-applied so the flagged entry is in view (spec §4.4), and the
  // "Unclear photos" chip pre-selected so only flagged entries show.
  applyDeepLink = () => {
    const state = this.props.location && this.props.location.state;
    if (!state || !state.pwDate) return;
    const date = parseYMD(state.pwDate);
    if (!date) return;
    this.setState({
      mode: MODE.SESSION,
      dateValue: date,
      byFilter: state.pwUserId || ALL_FILTER,
      photoFilter: state.pwUnclear ? PHOTO_FILTER.UNCLEAR : PHOTO_FILTER.ALL,
      empValue: null,
      empDay: null,
      query: '',
    });
    this.props.getPointWatchingByDate({
      locationId: this.locationId(),
      date: state.pwDate,
    });
  };

  // ---- mode / navigation ------------------------------------------------
  switchMode = (mode) => {
    if (mode === this.state.mode) return;
    this.setState({
      mode,
      byFilter: ALL_FILTER,
      photoFilter: PHOTO_FILTER.ALL,
      empDay: null,
      query: '',
    });
  };

  handleDateChange = (dateInput) => {
    const date = new Date(dateInput && dateInput.toString());
    if (date.toString() === 'Invalid Date') return;
    this.setState({
      dateValue: date,
      byFilter: ALL_FILTER,
      photoFilter: PHOTO_FILTER.ALL,
      query: '',
    });
    this.props.getPointWatchingByDate({
      locationId: this.locationId(),
      date: toYMD(date),
    });
  };

  handleEmployeeChange = (emp) => {
    this.setState({ empValue: emp, empDay: null, query: '' });
    if (emp && emp.userId) {
      this.props.getPointWatchingEmployeeDays({
        locationId: this.locationId(),
        userId: emp.userId,
      });
    }
  };

  handleChooseDay = (day) => {
    this.setState({ empDay: day, photoFilter: PHOTO_FILTER.ALL, query: '' });
    this.props.getPointWatchingDayEntries({
      locationId: this.locationId(),
      userId: this.state.empValue.userId,
      date: day.date,
    });
  };

  handleBackToDays = () => this.setState({ empDay: null, query: '' });

  setByFilter = (userId) => this.setState({ byFilter: userId });

  setPhotoFilter = (photoFilter) => this.setState({ photoFilter });

  setQuery = (query) => this.setState({ query });

  // Reload whichever list is currently on screen (used after clearing an
  // alert so the resolved entry's flag drops immediately).
  refreshCurrentView = () => {
    const { mode, empValue, empDay } = this.state;
    const locationId = this.locationId();
    if (mode === MODE.SESSION) {
      const ymd = this.sessionDateYMD();
      if (ymd) this.props.getPointWatchingByDate({ locationId, date: ymd });
    } else if (empValue && empDay) {
      this.props.getPointWatchingDayEntries({
        locationId,
        userId: empValue.userId,
        date: empDay.date,
      });
    }
  };

  // Dismiss a single "no clear subject" alert (audited server-side).
  handleClearAlert = (entry) => {
    if (!entry || !entry.pendingId) return;
    const user = this.props.user || {};
    this.props.clearPointWatchingAlert(
      {
        locationId: this.locationId(),
        pendingId: entry.pendingId,
        userId: user.userId,
        userName: user.displayName || user.firstname || user.username,
      },
      (err, message) => {
        if (err) {
          this.openFeedbackBar('error', err);
        } else {
          this.openFeedbackBar('success', message || 'Alert cleared');
          this.refreshCurrentView();
        }
      }
    );
  };

  openLightbox = (src, label) =>
    this.setState({ lightbox: { open: true, src, label } });
  closeLightbox = () =>
    this.setState({ lightbox: { open: false, src: null, label: null } });

  // ---- feedback bar -----------------------------------------------------
  openFeedbackBar = (status, message) =>
    this.setState({
      feedbackStatus: status,
      feedbackMessage: message,
      feedbackBarOpen: true,
    });

  closeFeedbackBar = () =>
    this.setState(
      { feedbackBarOpen: false, feedbackStatus: '', feedbackMessage: '' },
      () => this.props.clearStatus()
    );

  // ---- derived ----------------------------------------------------------
  matchesQuery = (e) => {
    const q = this.state.query.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.customerName || '').toLowerCase().includes(q) ||
      String(e.machine || '')
        .toLowerCase()
        .includes(q)
    );
  };

  // Photo-quality chip predicate: "unclear" is the camera flag itself
  // (missing photo / no human / blurry face) regardless of whether a
  // reviewer already cleared the alert.
  matchesPhotoFilter = (e) => {
    if (this.state.photoFilter === PHOTO_FILTER.CLEAR) return !e.noClearSubject;
    if (this.state.photoFilter === PHOTO_FILTER.UNCLEAR)
      return !!e.noClearSubject;
    return true;
  };

  // Entries after the employee chip + search box, but BEFORE the
  // photo-quality chips — the chip counts come from this list so they
  // don't zero themselves out when a chip is active.
  baseSessionEntries = () => {
    const entries = (this.props.byDate && this.props.byDate.entries) || [];
    return entries.filter((e) => {
      if (
        this.state.byFilter !== ALL_FILTER &&
        e.userId !== this.state.byFilter
      )
        return false;
      return this.matchesQuery(e);
    });
  };

  shownSessionEntries = () =>
    this.baseSessionEntries().filter(this.matchesPhotoFilter);

  baseDayEntries = () =>
    (this.props.dayEntries || []).filter(this.matchesQuery);

  shownDayEntries = () => this.baseDayEntries().filter(this.matchesPhotoFilter);

  sessionDateYMD = () => {
    const byDate = this.props.byDate;
    if (byDate && byDate.date) return byDate.date;
    return this.state.dateValue ? toYMD(this.state.dateValue) : '';
  };

  // ---- render pieces ----------------------------------------------------
  renderModeToggle() {
    const { mode } = this.state;
    const tab = (key, label, Icon) => {
      const active = mode === key;
      return (
        <button
          type="button"
          onClick={() => this.switchMode(key)}
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            borderRadius: 8,
            border: 'none',
            cursor: 'pointer',
            padding: '10px 0',
            fontSize: 14,
            fontWeight: 600,
            ...(active
              ? { backgroundColor: C.navy2, color: '#fff' }
              : { backgroundColor: 'transparent', color: C.ink }),
          }}
        >
          <Icon style={{ fontSize: 18 }} /> {label}
        </button>
      );
    };
    return (
      <div style={{ padding: '16px 0 0' }}>
        <div
          className="pw-control"
          style={{
            display: 'flex',
            borderRadius: 12,
            padding: 4,
            backgroundColor: C.track,
          }}
        >
          {tab(MODE.SESSION, 'By session', EventIcon)}
          {tab(MODE.EMPLOYEE, 'By employee', PersonIcon)}
        </div>
      </div>
    );
  }

  renderFilterRow() {
    const { mode, empDay } = this.state;
    // Hidden while viewing one employee's specific day (spec §4.3 / §7)
    if (mode === MODE.EMPLOYEE && empDay) return null;

    return (
      <div style={{ paddingTop: 16 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div className="pw-control" style={{ flex: 1 }}>
            {mode === MODE.SESSION ? (
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  disableFuture
                  variant="inline"
                  inputVariant="outlined"
                  label="Select a date"
                  format="EEEE, MMMM d, yyyy"
                  fullWidth
                  value={this.state.dateValue}
                  InputLabelProps={{ shrink: true }}
                  keyboardIcon={<CalendarTodayIcon style={{ color: C.ink }} />}
                  onChange={this.handleDateChange}
                />
              </MuiPickersUtilsProvider>
            ) : (
              <Autocomplete
                options={this.state.employees}
                getOptionLabel={(row) => row.firstname || ''}
                getOptionSelected={(opt, val) => opt.userId === val.userId}
                autoHighlight
                value={this.state.empValue}
                onChange={(event, value) => this.handleEmployeeChange(value)}
                renderOption={(row) => (
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <Avatar
                      name={row.firstname}
                      idKey={row.userId}
                      color={row.employeeColor}
                      size={28}
                    />
                    <span style={{ fontWeight: 600, color: C.navy2 }}>
                      {displayName(row.firstname)}
                    </span>
                  </div>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select an employee"
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                )}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  renderSessionResults() {
    const ymd = this.sessionDateYMD();
    if (!this.state.dateValue) {
      return (
        <EmptyPrompt
          icon={<EventIcon style={{ color: C.peri, fontSize: 32 }} />}
          title="Pick a date to start"
          text="Choose a day and every customer who got points that day shows up here, color-coded by the employee who gave them. Tap an employee to see only theirs."
        />
      );
    }

    const baseEntries = this.baseSessionEntries();
    const entries = this.shownSessionEntries();
    const employees = (this.props.byDate && this.props.byDate.employees) || [];
    const unclearCount = baseEntries.filter((e) => e.noClearSubject).length;

    return (
      <>
        <Banner
          left={`${weekdayOf(ymd)}, ${labelOf(ymd)}`}
          count={entries.length}
          unit={entries.length === 1 ? 'entry' : 'entries'}
        />

        <div className="pw-chip-row">
          <button
            type="button"
            onClick={() => this.setByFilter(ALL_FILTER)}
            className="pw-chip"
            style={
              this.state.byFilter === ALL_FILTER
                ? { backgroundColor: C.navy2, color: '#fff' }
                : { backgroundColor: C.track, color: C.ink }
            }
          >
            All
          </button>
          {employees.map((emp) => {
            const meta = empMeta(emp.userId, emp.name, emp.color);
            const active = this.state.byFilter === emp.userId;
            return (
              <button
                key={emp.userId}
                type="button"
                onClick={() => this.setByFilter(emp.userId)}
                className="pw-chip pw-chip-emp"
                style={
                  active
                    ? {
                        backgroundColor: meta.fill,
                        color: meta.ink,
                        borderColor: meta.ink,
                      }
                    : {
                        backgroundColor: '#fff',
                        color: C.navy2,
                        borderColor: '#e2e8f0',
                      }
                }
              >
                <span
                  style={{
                    height: 8,
                    width: 8,
                    borderRadius: '50%',
                    backgroundColor: meta.fill,
                    outline: `1px solid ${meta.ink}`,
                  }}
                />
                {displayName(emp.name)}
              </button>
            );
          })}
        </div>

        <PhotoQualityChips
          value={this.state.photoFilter}
          onChange={this.setPhotoFilter}
          clearCount={baseEntries.length - unclearCount}
          unclearCount={unclearCount}
        />

        <SearchBox query={this.state.query} setQuery={this.setQuery} />

        {entries.length === 0 ? (
          <div className="pw-empty-list">No entries match.</div>
        ) : (
          <div className="pw-card-stack">
            {entries.map((e) => (
              <EntryCard
                key={e.pendingId}
                e={e}
                onPickBy={(userId) => this.setByFilter(userId)}
                onExpand={this.openLightbox}
                onClearAlert={this.handleClearAlert}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  renderEmployeeResults() {
    const { empValue, empDay } = this.state;

    if (!empValue) {
      return (
        <EmptyPrompt
          icon={<PersonIcon style={{ color: C.peri, fontSize: 32 }} />}
          title="Pick an employee"
          text="Choose a person to see every day they worked, newest first. Then choose a day to open that session's points entries."
        />
      );
    }

    if (!empDay) {
      const days = this.props.employeeDays || [];
      return (
        <>
          <Banner
            left={displayName(empValue.firstname)}
            avatarKey={empValue.userId}
            avatarName={empValue.firstname}
            avatarColor={empValue.employeeColor}
            count={days.length}
            unit={days.length === 1 ? 'day worked' : 'days worked'}
          />
          <p
            style={{
              margin: '0 0 12px',
              padding: '0 4px',
              fontSize: 14,
              fontWeight: 500,
              color: '#64748b',
            }}
          >
            Choose a day to see {displayName(empValue.firstname)}'s entries
            {days.length >= 14 ? ' (latest 14 days)' : ''}
          </p>
          {days.length === 0 ? (
            <div className="pw-empty-list">No sessions found.</div>
          ) : (
            <div className="pw-card-stack">
              {days.map((d, i) => (
                <EmployeeDayCard
                  key={`${d.date}-${i}`}
                  d={d}
                  onClick={() => this.handleChooseDay(d)}
                />
              ))}
            </div>
          )}
        </>
      );
    }

    const baseEntries = this.baseDayEntries();
    const entries = this.shownDayEntries();
    const unclearCount = baseEntries.filter((e) => e.noClearSubject).length;
    return (
      <>
        <Banner
          left={`${weekdayOf(empDay.date)}, ${labelOf(empDay.date)}`}
          avatarKey={empValue.userId}
          avatarName={empValue.firstname}
          avatarColor={empValue.employeeColor}
          count={entries.length}
          unit={entries.length === 1 ? 'entry' : 'entries'}
        />
        <PhotoQualityChips
          value={this.state.photoFilter}
          onChange={this.setPhotoFilter}
          clearCount={baseEntries.length - unclearCount}
          unclearCount={unclearCount}
        />
        <SearchBox query={this.state.query} setQuery={this.setQuery} />
        {entries.length === 0 ? (
          <div className="pw-empty-list">No entries match.</div>
        ) : (
          <div className="pw-card-stack">
            {entries.map((e) => (
              <EntryCard
                key={e.pendingId}
                e={e}
                showBy={false}
                onExpand={this.openLightbox}
                onClearAlert={this.handleClearAlert}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  render() {
    const headerButtons = [];
    // Back button lives in the toolbar's contextual menu, matching the
    // other screens. Only shown while viewing one employee's specific day.
    // (Alerts live in the global navbar bell, so no alert button here.)
    if (this.state.mode === MODE.EMPLOYEE && this.state.empDay) {
      headerButtons.push({
        icon: <KeyboardBackspaceIcon />,
        handler: this.handleBackToDays,
        title: 'Back',
      });
    }
    return (
      <React.Fragment>
        <FeedbackBar
          open={this.state.feedbackBarOpen}
          handleClose={this.closeFeedbackBar}
          severity={this.state.feedbackStatus}
          message={this.state.feedbackMessage}
        />

        <CustomToolbar title="Points Watching" headerButtons={headerButtons} />

        <div className="pw-shell">
          {this.renderModeToggle()}
          {this.renderFilterRow()}

          <div className="pw-results">
            {this.state.mode === MODE.SESSION
              ? this.renderSessionResults()
              : this.renderEmployeeResults()}
          </div>
        </div>

        <Lightbox
          open={this.state.lightbox.open}
          src={this.state.lightbox.src}
          label={this.state.lightbox.label}
          onClose={this.closeLightbox}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  status: statusSelector(state),
  user: userInfoSelector(state),
  employees: employeesSelector(state),
  byDate: pointWatchingByDateSelector(state),
  employeeDays: pointWatchingEmployeeDaysSelector(state),
  dayEntries: pointWatchingDayEntriesSelector(state),
});

const actionCreators = {
  clearStatus,
  getEmployees,
  getPointWatchingByDate,
  getPointWatchingEmployeeDays,
  getPointWatchingDayEntries,
  clearPointWatchingAlert,
};

export default withShipment(
  { actionCreators, mapStateToProps },
  PointsWatching
);
